import { VOCAB } from '../vocab.js'

export default {
	ids: [
		'wave_height',
		'wave_length',
		'shape',
		'wave_speed',
		'wave_b_length',
		'wave_b_height',
		'wave_b_speed',
		'wave_c_length',
		'wave_c_height',
		'wave_c_speed',
		'wave_direction',
		'hill_rows',
		'blobs',
		'blob_size',
		'blob_soft',
		'temperature',
		'stretch',
	],
	glsl: `
// Sine at 0, triangle at 1, on a shared unit period so the mix never slips phase.
float wave(float x) {
	float s = sin(x * TAU);
	float t = 4.0 * abs(fract(x - 0.25) - 0.5) - 1.0;
	return mix(s, t, clamp(uShape, 0.0, 1.0));
}

// A fractional count crossfades into its own continuation over the last wavelength, so the seam never breaks.
float ringWave(float u, float n) {
	float w = min(1.0, 1.0 / max(n, 1e-3));
	return mix(wave(n * u), wave(n * (u - 1.0)), smoothstep(1.0 - w, 1.0, u));
}

// The big wave along the bands, travelling with wave_speed (its phase in waves). Round the ring at enclose 1, one whole count for every ring, faded where a wave falls under a few pixels. Stage units across the bands. U3: wave_speed round the ring.
float waveAt(Surf s) {
	float amp = uWaveHeight * s.unit;
	float along = amp * wave(s.l / (uWaveLength * s.unit) - uWaveSpeed);
	if (uEnclose < 1e-4) return along;
	float count = s.ringLen / (uWaveLength * s.unit);
	float n = floor(count) + smoothstep(0.4, 0.6, fract(count));
	float ringAmp = amp * (1.0 - smoothstep(0.15, 0.45, s.turnsPerPx * n));
	float ring = ringAmp * ringWave(fract(atan(s.ringTurn.y, s.ringTurn.x) / TAU + uAngle / 360.0), n);
	return mix(along, ring, uEnclose);
}

// Blobs drifting on temperature's track: whole turns per BLOB_TURN (its cycle, 32), so the fold never seams. Up to MAX_BLOBS, each with its own orbit and size.
const int MAX_BLOBS = 6;
const float BLOB_TURN = 32.0;
const float BLOB_REACH = 0.36;

float blobHash(float i) {
	return fract(sin(i * 12.9898 + 4.1414) * 43758.5453);
}

// Polynomial smooth min over k; k 0 is the hard min (an exact distance, creased where rings meet).
float smin(float a, float b, float k) {
	if (k <= 0.0) return min(a, b);
	float m = max(k - abs(a - b), 0.0) / k;
	return min(a, b) - m * m * k * 0.25;
}

// Temperature's rate (per second, base included) under which the blobs gather to the centre: at 0 every blob sits there, still.
const float BLOB_PULL = 0.15;

// A distance field, stage units: the smooth union of blobs circles, so its level lines are equidistant (|grad| 1) away from the creases. Each blob's rings sit off the first's by its share off blob_size (each 0.55–1.45× that), so a size change mostly reshapes where blobs melt. A fractional count blends the last blob in. The contours' height is the half frame less that distance, peaking at the blobs: one blob at the centre is enclosed stripes' round rings (side − |q|) line for line.
// Stretch s > 0 is a smooth change of each blob's coordinates, never a blend of two fields, so every line stays smooth and the pitch across the bands holds. Three stages: line up (s 0 → 0.6), each blob's across offset sliding to the bands' own coordinate P, so they stand on one column; stretch (0 → 0.75), each blob's coordinate along the bands squeezed to 0 and its size offset to 0, so its loops draw out into ellipses and then into one straight fold along the bands, the blobs' softness keeping where they meet smooth; sweep (0.6 → 1), the fold sliding a diagonal ahead across the bands, off the frame, the bands behind it. At s 1 every blob's distance is −P and the height P: the bands. Returned less P, so the lattice (P + h) reads the height.
float blobsAt(Surf s) {
	float n = min(uBlobs, float(MAX_BLOBS));
	float k = uBlobSoft * s.unit;
	float t = TAU * uTemperature / BLOB_TURN;
	vec2 centre = 0.5 * uResolution;
	float side = max(0.5 * min(uResolution.x, uResolution.y), 1.0);
	float reach = BLOB_REACH * min(uResolution.x, uResolution.y) * uZoom * smoothstep(0.0, BLOB_PULL, abs(uTemperatureRate));
	// The bands' coordinate before the fold: the lattice folds the finished height, so past the crease the blobs never mirror.
	float P = s.near + s.rim - s.anchor;
	float r0 = blobHash(41.0);
	vec2 nrm = vec2(0.0, 1.0);
	float lineUp = 0.0;
	float len = 0.0;
	float sweep = 0.0;
	if (uStretch > 0.0) {
		vec2 px = dFdx(s.p);
		vec2 py = dFdy(s.p);
		vec2 g = inverse(mat2(px.x, py.x, px.y, py.y)) * vec2(dFdx(P), dFdy(P));
		nrm = g / max(length(g), 1e-6);
		lineUp = smoothstep(0.0, 0.6, uStretch);
		len = smoothstep(0.0, 0.75, uStretch);
		sweep = smoothstep(0.6, 1.0, uStretch);
	}
	// The sweep takes the half frame off the bands' place and hands it to their travel, so the bands behind the fold hold still against the flow; a build switch carries it into speed's travel (common.js fieldLead).
	fieldLead = sweep * side / (max(uWidth + uSpacing, 1e-4) * s.unit);
	float diag = length(uResolution);
	// The along-band squeeze of each blob's coordinate, 1 → 0: geometric in len (each step stretches the loops by one factor) down to a blob size over the diagonal, then the last of it linearly to 0.
	float scale = max(uBlobSize * s.unit, 1.0);
	float squeeze = (1.0 - len) * pow(scale / (scale + diag), len);
	float d = 0.0;
	// The union's own depth: smin of equal distances lies under them by a constant, so the field is less the same chain over zeros (smin is shift-invariant): at stretch 1, where every blob's distance is −P, exactly −P.
	float z = 0.0;
	for (int i = 0; i < MAX_BLOBS; i++) {
		float fi = float(i);
		float w = clamp(n - fi, 0.0, 1.0);
		if (w <= 0.0) break;
		float a = blobHash(fi);
		float b = blobHash(fi + 17.0);
		vec2 m = vec2(1.0 + mod(fi, 3.0), 1.0 + mod(fi + 1.0, 2.0));
		vec2 o = vec2(sin(m.x * t + TAU * a) * uResolution.x / min(uResolution.x, uResolution.y), sin(m.y * t + TAU * b));
		float r = uBlobSize * s.unit * 0.9 * (blobHash(fi + 41.0) - r0);
		vec2 q = s.p - centre - reach * o;
		float di;
		if (uStretch > 0.0) {
			// Across: lined up on P, then swept ahead; along: squeezed, so the loops are ellipses drawn out along the bands.
			float a = mix(dot(q, nrm), P, lineUp) - sweep * diag;
			float along = squeeze * dot(q, vec2(-nrm.y, nrm.x));
			di = length(vec2(a, along)) - (1.0 - len) * r - sweep * diag;
		} else di = length(q) - r;
		d = i == 0 ? di : mix(d, smin(d, di, k), w);
		z = i == 0 ? 0.0 : mix(z, smin(z, 0.0, k), w);
	}
	return (1.0 - sweep) * side - (d - z) - P;
}

float displacement(Surf s) {
	float h = waveAt(s);
	if (uBlobs > 0.0) h += blobsAt(s);
	return h;
}

// Rows' sea in field cells of SEA_CELL su (across the rows hill_rows cells a row), read from the camera: three waves, A (wave_height, wave_length), B and C, each three trains at the runtime's phases (sea.js: each wave's travel and the camera's), each row's running ahead by its parallax offset (uSeaRow, a ring by row number).
const float SEA_CELL = 137.5;
const int SEA_RING = ${VOCAB.count.maximum + 6};
uniform vec3 uSeaPhaseA;
uniform vec3 uSeaPhaseB;
uniform vec3 uSeaPhaseC;
uniform vec4 uSeaRow[3 * SEA_RING];

// A wave at length su: train 2 at +60°, train 3 at −60°, train 1 their sum, so its peaks stay round; faded where a wave falls under a few pixels (fine: cells per pixel).
float trio(vec2 q, float len, vec3 ph, float fine) {
	float m = SEA_CELL / max(len, 1.0);
	float fade = 1.0 - smoothstep(0.3, 0.6, fine * m);
	if (fade <= 0.0) return 0.0;
	vec2 k2 = m * vec2(0.5, 0.8660254);
	vec2 k3 = m * vec2(0.5, -0.8660254);
	return fade * (wave(dot(q, k2 + k3) + ph.x) + wave(dot(q, k2) + ph.y) + wave(dot(q, k3) + ph.z));
}

// Row number row's crest at field point f, stage units up: three trains peak at 1.5 × each height.
float rowCrestAt(vec2 f, float row, float fine, float unit) {
	if (uWaveHeight == 0.0 && uWaveBHeight == 0.0 && uWaveCHeight == 0.0) return 0.0;
	int r = 3 * int(mod(row, float(SEA_RING)));
	// One branch: per-wave guards cost more with all three on than they save.
	float h = uWaveHeight * trio(f, uWaveLength, uSeaPhaseA + uSeaRow[r].xyz, fine);
	if (uWaveBHeight != 0.0 || uWaveCHeight != 0.0) h += uWaveBHeight * trio(f, uWaveBLength, uSeaPhaseB + uSeaRow[r + 1].xyz, fine)
		+ uWaveCHeight * trio(f, uWaveCLength, uSeaPhaseC + uSeaRow[r + 2].xyz, fine);
	return unit * h / 2.0;
}

// rowCrestAt for sine waves at the row's own place (f.y = place · hill_rows), from its closed form (sea.js crestRows): one sin and cos per wave.
uniform vec4 uSeaCrest[3 * SEA_RING];
float trioAlong(float x, float len, vec4 c, float fine) {
	float m = SEA_CELL / max(len, 1.0);
	float fade = 1.0 - smoothstep(0.3, 0.6, fine * m);
	if (fade <= 0.0) return 0.0;
	float th = 0.5 * TAU * m * x;
	float s = sin(th);
	float co = cos(th);
	return fade * (2.0 * s * co * c.x + (co * co - s * s) * c.y + s * c.z + co * c.w);
}
float rowCrestAlong(float x, float row, float fine, float unit) {
	if (uWaveHeight == 0.0 && uWaveBHeight == 0.0 && uWaveCHeight == 0.0) return 0.0;
	int r = 3 * int(mod(row, float(SEA_RING)));
	float h = uWaveHeight * trioAlong(x, uWaveLength, uSeaCrest[r], fine);
	if (uWaveBHeight != 0.0 || uWaveCHeight != 0.0) h += uWaveBHeight * trioAlong(x, uWaveBLength, uSeaCrest[r + 1], fine)
		+ uWaveCHeight * trioAlong(x, uWaveCLength, uSeaCrest[r + 2], fine);
	return unit * h / 2.0;
}
`,
}
